import React, { useState, useEffect } from "react";
import { Button, LoadPanel } from "devextreme-react";
import SSOSecurity from "../../components/security/SSOSecurity";
import PasswordSecurity from "../../components/security/PasswordSecurity";
import MultiFactorSecurity from "../../components/security/MultiFactorSecurity";
import { useAuth } from "../../contexts/auth";
import Server from "../../api/Server";
import { alert } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import { loading } from "../../components/helpers/index";
import SignOnPages from "../../components/security/SignOnPages";
import UnAuthorized from "../../common/UnAuthorized";
import PassworReset from "../../components/security/PassworReset";
import MandatoryPasswordChange from "../../components/security/MandatoryPasswordChange";
import PasswordHistory from "../../components/security/PasswordHistory";

// {
//   enablePasswordLogin: true,
//   sso_enabled: true,
//   sso_authority_endpoint: "",
//   sso_directory_id: "",
//   sso_application_id: "",
//   two_factor_enabled: false,
//   two_factor_channels: [],
//   two_factor_keys_as_words: false,
//   use_login_app: false,
//   two_factor_mandatory: false,
//   enable_security_emails: false,
//   admin_2fa_notification_emails: "",
//   admin_password_reset_notifications_emails: "",
//   two_factor_disable_user_email_edit: false,
// }

const LoginSecurity = () => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { user, userRightAuth } = useAuth();

  useEffect(() => {
    getConfigs();
  }, []);

  const getConfigs = async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await Server.getLoginSettingsConfig(user.accountAdminUrl, {
        clientCode: user.clientCode,
        sessionKey: user.sessionKey,
      });

      let enablePasswordLogin = true;
      let sso_enabled = false;

      const loginMethods = data["allowed_login_methods"];
      if (loginMethods) {
        const value = loginMethods.value;
        if (value.length) {
          if (value.includes("pwd")) {
            enablePasswordLogin = true;
          } else {
            enablePasswordLogin = false;
          }

          if (value.includes("sso")) {
            sso_enabled = true;
          } else {
            sso_enabled = false;
          }
        } else {
          enablePasswordLogin = true;
        }
      }

      const newState = {
        enablePasswordLogin,
        sso_enabled:
          (data["sso_enabled"] && data["sso_enabled"].value) || sso_enabled
            ? true
            : false,
        sso_authority_endpoint: data["sso_authority_endpoint"]
          ? data["sso_authority_endpoint"].value
          : "",
        sso_directory_id: data["sso_directory_id"]
          ? data["sso_directory_id"].value
          : "",
        sso_application_id: data["sso_application_id"]
          ? data["sso_application_id"].value
          : "",
        two_factor_enabled: data["two_factor_enabled"]
          ? data["two_factor_enabled"].value
          : false,
        two_factor_channels: data["two_factor_channels"]
          ? data["two_factor_channels"].value
          : [],

        two_factor_keys_as_words: data["two_factor_keys_as_words"]
          ? data["two_factor_keys_as_words"].value
          : false,
        use_login_app: data["use_login_app"]
          ? data["use_login_app"].value
          : false,
        two_factor_mandatory: data["two_factor_mandatory"]
          ? data["two_factor_mandatory"].value
          : false,
        enable_security_emails: data["enable_security_emails"]
          ? data["enable_security_emails"].value
          : false,
        two_factor_disable_user_email_edit: data[
          "two_factor_disable_user_email_edit"
        ]
          ? data["two_factor_disable_user_email_edit"].value
          : false,
        admin_2fa_notification_emails:
          data["admin_2fa_notification_emails"] &&
          data["admin_2fa_notification_emails"].value?.length
            ? data["admin_2fa_notification_emails"].value.join(",")
            : "",
        admin_password_reset_notifications_emails:
          data["admin_password_reset_notifications_emails"] &&
          data["admin_password_reset_notifications_emails"].value?.length
            ? data["admin_password_reset_notifications_emails"].value.join(",")
            : "",
        admin_password_special_character_required: data[
          "admin_password_special_character_required"
        ]
          ? data["admin_password_special_character_required"].value
          : false,
        admin_password_min_length: data["admin_password_min_length"]
          ? data["admin_password_min_length"].value === 0
            ? ""
            : data["admin_password_min_length"].value
          : "",
        rotate_passwords_enabled: data["rotate_passwords_enabled"]
          ? data["rotate_passwords_enabled"].value
          : false,
        rotate_passwords_days: data["rotate_passwords_days"]
          ? data["rotate_passwords_days"].value === 0
            ? ""
            : data["rotate_passwords_days"].value
          : "",
        password_history_enabled: data["password_history_enabled"]
          ? data["password_history_enabled"].value
          : false,
        password_history_entries: data["password_history_entries"]
          ? data["password_history_entries"].value === 0
            ? ""
            : data["password_history_entries"].value
          : "",

        password_special_character_required: data[
          "password_special_character_required"
        ]
          ? data["password_special_character_required"].value
          : false,
      };

      setSettings((prev) => ({ ...prev, ...newState }));
      setLoading(false);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error getting settings, please try again", "error", 3000);
        }
      } else {
        notify("Error getting settings, please try again", "error", 3000);
      }
      setLoading(false);
    }
  };

  const onValueChange = (e, name) => {
    const { value } = e;

    setSettings({ ...settings, [name]: value });
  };

  const saveConfig = async () => {
    const {
      enablePasswordLogin,
      sso_enabled,
      sso_application_id,
      sso_authority_endpoint,
      sso_directory_id,
    } = settings;
    if (!enablePasswordLogin && !sso_enabled) {
      alert(
        "If both passwords and Single Sign-On are disabled, users will be unable to log in. Please select at least one.",
        "At Least One Login Method Required"
      );
      return;
    }

    if (
      sso_enabled &&
      (!sso_application_id || !sso_authority_endpoint || !sso_directory_id)
    ) {
      alert(
        "Authority URL, Directory ID and Application ID are required to enable Single Sign-On ",
        "Login Setting"
      );
      return;
    }

    setIsSaving(true);

    const loginMethods = [];

    if (enablePasswordLogin) {
      loginMethods.push("pwd");
    }

    if (sso_enabled) {
      loginMethods.push("sso");
    }

    const values = {
      allowed_login_methods: { value: loginMethods },
    };
    for (let key in settings) {
      if (settings.hasOwnProperty(key) && key !== "enablePasswordLogin") {
        if (key === "admin_2fa_notification_emails") {
          if (settings[key]) {
            values[key] = {
              value: settings[key].split(","),
            };
          } else {
            values[key] = {
              value: [],
            };
          }
        } else if (key === "admin_password_reset_notifications_emails") {
          if (settings[key]) {
            values[key] = {
              value: settings[key].split(","),
            };
          } else {
            values[key] = {
              value: [],
            };
          }
        } else if (
          key === "admin_password_min_length" ||
          key === "rotate_passwords_days" ||
          key === "password_history_entries"
        ) {
          if (settings[key]) {
            values[key] = {
              value: settings[key],
            };
          } else {
            values[key] = {
              value: 0,
            };
          }
        } else {
          values[key] = {
            value: settings[key],
          };
        }
      }
    }

    try {
      await Server.saveLoginSettingsConfig(values, user.accountAdminUrl, {
        clientCode: user.clientCode,
        sessionKey: user.sessionKey,
      });
      notify("Settings updated successfully", "success", 3000);
      setIsSaving(false);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error saving settings, please try again", "error", 3000);
        }
      } else {
        notify("Error saving settings, please try again", "error", 3000);
      }
      setIsSaving(false);
    }
  };

  const isUserPermitted = () => {
    const isFound = userRightAuth?.find(
      (e) => e.sectionName === "account_security_settings"
    );

    if (isFound && isFound.view === 2) {
      return true;
    }

    return false;
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadPanel visible />
      ) : (
        <>
          {!userRightAuth ? (
            <>
              <UnAuthorized
                message={`An Error occured why getting user rights. Please try again`}
              />
            </>
          ) : (
            <>
              {userRightAuth && isUserPermitted() && settings ? (
                <>
                  <h2 className={"content-block"}>
                    Account Login & Security Settings
                  </h2>
                  <div className={"content-block"}>
                    <div className='d-flex justify-content-end mb-3'>
                      <Button
                        type='default'
                        stylingMode='contained'
                        onClick={saveConfig}
                      >
                        Save Changes
                      </Button>
                    </div>
                    {isSaving ? loading("Saving") : null}
                    <span className='mb-4 text-dark fs-5'>Sign-On Pages</span>
                    <SignOnPages
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>Password Login</span>
                    <PasswordSecurity
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>Password Reset</span>
                    <PassworReset
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>Single Sign-On </span>
                    <SSOSecurity
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>
                      Multi-Factor Authentication
                    </span>
                    <MultiFactorSecurity
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>
                      Mandatory password change
                    </span>
                    <MandatoryPasswordChange
                      value={settings}
                      onValueChange={onValueChange}
                    />
                    <span className='mb-4 text-dark fs-5'>
                      Password history
                    </span>
                    <PasswordHistory
                      value={settings}
                      onValueChange={onValueChange}
                    />
                  </div>
                </>
              ) : (
                <>
                  {!isUserPermitted() ? (
                    <UnAuthorized
                      message={
                        "This page requires the “Account Security Settings” permission."
                      }
                    />
                  ) : (
                    <>
                      {" "}
                      {!isLoading && !settings ? (
                        <UnAuthorized message={"Error getting configuration"} />
                      ) : null}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default LoginSecurity;
